import React from "react";
import { ReactComponent as IconWarning } from "../assets/Icons/Icon_Warning.svg";
import { useMaintenanceForCurrentProduct } from "@shared/hooks/useMaintenanceStatus";

/**
 * Notice shown while a maintenance window is open on the product the user is
 * currently looking at (CTX-483).
 *
 * Deliberately not dismissible. The tenant notice banner lets `info` and
 * `notice` be closed, and this is neither: while the window is open the rest
 * of the page cannot show live status, so a reader who dismissed this would
 * be left with greyed-out cards and no explanation.
 *
 * Kept separate from TenantNoticeBanner rather than folded into it: that one
 * is per-tenant and carries whatever an operator wrote for one customer;
 * this one is platform-wide, per-product, and drives how the rest of the UI
 * reads. Merging them would make both harder to reason about.
 */
const MaintenanceBanner: React.FC = () => {
  const window = useMaintenanceForCurrentProduct();

  if (!window) return null;

  const until = new Date(window.until);
  const untilLabel = Number.isNaN(until.getTime())
    ? null
    : until.toLocaleString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        day: "numeric",
        month: "short",
      });

  return (
    <div
      className="
        bg-[var(--color-semantic-bg-warning-secondary)]
        text-[var(--color-semantic-text-warning)]
        border-b
        border-[var(--color-utility-warning-600)]
        px-4
        py-3
        w-full
        flex
        items-start
        gap-3
        z-15
      "
      role="alert"
    >
      <IconWarning className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{window.message}</p>
        {untilLabel && (
          <p className="text-xs font-normal opacity-90 mt-0.5">
            Expected to end at {untilLabel}.
          </p>
        )}
      </div>
    </div>
  );
};

export default MaintenanceBanner;
